import React, { useState, useEffect, useCallback } from 'react';
import {
  getDouanePos, getDouanePo, createDouanePo, updateDouanePo, deleteDouanePo, getDouanePoPdfUrl,
  searchCars, getPorts,
} from '../../services/api';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Plus, Pencil, Trash, FilePdf, MagnifyingGlass, X, Receipt } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import CustomFeesEditor from './CustomFeesEditor';

const fmtDZD = (v) => `${Math.round(parseFloat(v || 0)).toLocaleString()} DZD`;

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-700',
  issued: 'bg-blue-100 text-blue-700',
  paid: 'bg-green-100 text-green-700',
  closed: 'bg-gray-200 text-gray-700',
};

const DouanePosPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const canDelete = isAdmin || user?.role === 'logistics_manager';
  const canEdit = isAdmin || ['logistics_manager', 'operations'].includes(user?.role);
  const canSeeInternal = user?.can_see_purchase_price === true || isAdmin;

  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ports, setPorts] = useState([]);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create
  const [form, setForm] = useState({ title: '', port_id: '', notes: '', status: 'draft' });
  const [selectedCars, setSelectedCars] = useState([]); // [{order_id, car_id, label, ...}]

  // Car picker
  const [pickerQuery, setPickerQuery] = useState('');
  const [pickerResults, setPickerResults] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [res, pt] = await Promise.all([getDouanePos(), getPorts().catch(() => ({ data: [] }))]);
      setPos(res.data || []);
      setPorts(pt.data || []);
    } catch {
      toast.error('Failed to load Douane POs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ title: '', port_id: '', notes: '', status: 'draft' });
    setSelectedCars([]);
    setPickerQuery('');
    setPickerResults([]);
    setEditorOpen(true);
  };

  const openEdit = async (po) => {
    try {
      const res = await getDouanePo(po.id);
      const full = res.data;
      setEditing(full);
      setForm({
        title: full.title || '',
        port_id: full.port_id || '',
        notes: full.notes || '',
        status: full.status || 'draft',
      });
      setSelectedCars(
        (full.cars || []).map(c => ({
          order_id: c.order_id, car_id: c.car_id,
          label: `${c.brand || ''} ${c.model || ''}${c.vin ? ' · ' + c.vin : ''}`.trim(),
          client_name: c.client_name, order_number: c.order_number,
          fees_total: c.fees_total,
          custom_fees: c.custom_fees || [],
          custom_fees_original: JSON.stringify(c.custom_fees || []), // for change detection
        }))
      );
      setPickerQuery('');
      setPickerResults([]);
      setEditorOpen(true);
    } catch {
      toast.error('Failed to load Douane PO');
    }
  };

  const handleDelete = async (po) => {
    if (!window.confirm(`Delete Douane PO ${po.po_number}?`)) return;
    try {
      await deleteDouanePo(po.id);
      toast.success('Douane PO deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const runSearch = async () => {
    setPickerLoading(true);
    try {
      const res = await searchCars({ q: pickerQuery, unassigned_only: false, limit: 30 });
      setPickerResults(res.data?.cars || []);
    } catch {
      toast.error('Search failed');
    } finally {
      setPickerLoading(false);
    }
  };

  const addCar = async (c) => {
    if (selectedCars.find(s => s.car_id === c.id)) {
      toast.info('Car already added to this PO');
      return;
    }
    // Fetch the car's existing custom_fees so editor opens with current values
    let existingFees = [];
    try {
      const res = await api.get(`/orders/${c.order_id}/cars/${c.id}`);
      existingFees = res.data?.custom_fees || [];
    } catch { /* non-fatal */ }
    setSelectedCars(prev => [...prev, {
      order_id: c.order_id, car_id: c.id,
      label: `${c.brand || ''} ${c.model || ''}${c.vin ? ' · ' + c.vin : ''}`.trim(),
      client_name: c.client_name, order_number: c.order_number,
      custom_fees: existingFees,
      custom_fees_original: JSON.stringify(existingFees),
    }]);
  };

  const setCarFees = (carId, fees) => {
    setSelectedCars(prev => prev.map(c => c.car_id === carId ? { ...c, custom_fees: fees } : c));
  };

  const removeCar = (carId) => setSelectedCars(prev => prev.filter(c => c.car_id !== carId));

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      title: form.title.trim() || null,
      port_id: form.port_id || null,
      notes: form.notes.trim() || null,
      cars: selectedCars.map(c => ({ order_id: c.order_id, car_id: c.car_id })),
    };
    try {
      // 1) Save each car's fees that changed (fees live on the car, not on the PO)
      const feeWrites = selectedCars
        .filter(c => JSON.stringify(c.custom_fees || []) !== (c.custom_fees_original || '[]'))
        .map(c => api.put(`/orders/${c.order_id}/cars/${c.car_id}`, {
          custom_fees: (c.custom_fees || [])
            .filter(f => f.name)
            .map(f => ({
              name: f.name,
              amount: parseFloat(f.amount) || 0,
              internal_amount: parseFloat(f.internal_amount) || 0,
              is_paid: !!f.is_paid,
            })),
        }));
      if (feeWrites.length) {
        await Promise.all(feeWrites);
      }
      // 2) Save the PO metadata + car list
      if (editing) {
        await updateDouanePo(editing.id, { ...payload, status: form.status });
        toast.success(`Douane PO updated · ${feeWrites.length ? feeWrites.length + ' car fees saved' : 'no fee changes'}`);
      } else {
        await createDouanePo(payload);
        toast.success('Douane PO created');
      }
      setEditorOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const openPdf = (po) => window.open(getDouanePoPdfUrl(po.id), '_blank');

  return (
    <div className="space-y-4" data-testid="douane-pos-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Receipt weight="duotone" className="w-7 h-7 text-primary" />
            Douane POs
          </h1>
          <p className="text-sm text-muted-foreground">Customs clearance purchase orders — group cars and bill their customs fees as a separate document.</p>
        </div>
        {canEdit && (
          <Button onClick={openCreate} data-testid="add-douane-po-btn">
            <Plus weight="bold" className="w-4 h-4 mr-1" /> New Douane PO
          </Button>
        )}
      </div>

      <div className="bg-white border rounded-md overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : pos.length === 0 ? (
          <div className="p-10 text-center">
            <Receipt weight="duotone" className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
            <p className="font-medium">No Douane POs yet</p>
            <p className="text-sm text-muted-foreground">Create one to group customs clearance fees across multiple cars.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">PO #</th>
                <th className="px-3 py-2 text-left">Title</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-right">Cars</th>
                <th className="px-3 py-2 text-right">Total Fees</th>
                <th className="px-3 py-2 text-left">Created</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pos.map(po => (
                <tr key={po.id} className="border-t hover:bg-slate-50" data-testid={`douane-row-${po.id}`}>
                  <td className="px-3 py-2 font-mono font-semibold">{po.po_number}</td>
                  <td className="px-3 py-2">{po.title || <span className="text-muted-foreground">—</span>}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${STATUS_COLORS[po.status] || 'bg-slate-100'}`}>
                      {(po.status || 'draft').toUpperCase()}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">{po.car_count || 0}</td>
                  <td className="px-3 py-2 text-right font-semibold">{fmtDZD(po.customs_total)}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {po.created_at ? new Date(po.created_at).toLocaleDateString() : '-'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex gap-1">
                      <Button size="sm" variant="ghost" title="Download PDF" onClick={() => openPdf(po)} data-testid={`douane-pdf-${po.id}`}>
                        <FilePdf className="w-4 h-4" />
                      </Button>
                      {canEdit && (
                        <Button size="sm" variant="ghost" title="Edit" onClick={() => openEdit(po)} data-testid={`douane-edit-${po.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                      )}
                      {canDelete && (
                        <Button size="sm" variant="ghost" className="text-destructive" title="Delete" onClick={() => handleDelete(po)} data-testid={`douane-delete-${po.id}`}>
                          <Trash className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Editor Dialog */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.po_number}` : 'New Douane PO'}</DialogTitle>
            <DialogDescription>
              Bill customs clearance fees as a separate document. Add cars from existing orders — their `custom_fees` will be totaled in the PDF.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Title</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Dealer Y · May container batch" data-testid="douane-title-input" />
              </div>
              <div>
                <Label className="text-xs">Port</Label>
                <Select value={form.port_id || 'none'} onValueChange={(v) => setForm({ ...form, port_id: v === 'none' ? '' : v })}>
                  <SelectTrigger className="h-9" data-testid="douane-port-select"><SelectValue placeholder="— None —" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {ports.map(p => <SelectItem key={p.id} value={p.id}>{p.name}{p.city ? ` (${p.city})` : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {editing && (
                <div>
                  <Label className="text-xs">Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="issued">Issued</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="douane-notes-input" />
            </div>

            {/* Car picker */}
            <div className="border rounded p-3 bg-slate-50">
              <Label className="text-xs font-bold">Add cars to this PO</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={pickerQuery}
                  onChange={(e) => setPickerQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); runSearch(); } }}
                  placeholder="Search by client name, order # or VIN…"
                  className="h-8 text-sm"
                  data-testid="douane-car-search"
                />
                <Button type="button" size="sm" onClick={runSearch} disabled={pickerLoading} data-testid="douane-car-search-btn">
                  <MagnifyingGlass className="w-4 h-4 mr-1" /> {pickerLoading ? '…' : 'Search'}
                </Button>
              </div>
              {pickerResults.length > 0 && (
                <div className="mt-2 max-h-44 overflow-y-auto space-y-1 border bg-white rounded">
                  {pickerResults.map(c => {
                    const already = !!selectedCars.find(s => s.car_id === c.id);
                    return (
                      <div key={c.id} className="flex items-center justify-between px-2 py-1.5 text-xs hover:bg-slate-50 border-b last:border-b-0">
                        <div className="flex-1 min-w-0">
                          <span className="font-medium">{c.brand} {c.model}</span>
                          {c.color && <span className="text-muted-foreground"> · {c.color}</span>}
                          {c.vin && <span className="font-mono text-[10px] text-muted-foreground ml-1">({c.vin})</span>}
                          <span className="text-muted-foreground"> — {c.order_number} · {c.client_name || 'Client?'}</span>
                          {c.container_number && <Badge variant="outline" className="ml-1 text-[9px]">{c.container_number}</Badge>}
                        </div>
                        <Button type="button" size="sm" variant={already ? 'ghost' : 'outline'} disabled={already} onClick={() => addCar(c)} data-testid={`douane-add-car-${c.id}`}>
                          {already ? 'Added' : '+ Add'}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Selected cars list with inline fee editor */}
            <div>
              <Label className="text-xs">Selected cars ({selectedCars.length})</Label>
              {selectedCars.length === 0 ? (
                <p className="text-[11px] text-muted-foreground italic mt-1">No cars yet — search and add cars above.</p>
              ) : (
                <div className="space-y-2 mt-1">
                  {selectedCars.map(c => (
                    <details key={c.car_id} className="border rounded bg-white" data-testid={`douane-selected-${c.car_id}`} open>
                      <summary className="flex items-center justify-between px-2 py-2 cursor-pointer hover:bg-slate-50 text-xs">
                        <div className="flex-1 min-w-0">
                          <span className="font-medium">{c.label}</span>
                          <span className="text-muted-foreground ml-2">
                            {c.order_number || ''}{c.client_name ? ` · ${c.client_name}` : ''}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="text-[10px]">
                            {(c.custom_fees || []).reduce((s, f) => s + (parseFloat(f.amount) || 0), 0).toLocaleString()} DZD
                          </Badge>
                          <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); removeCar(c.car_id); }}
                            className="text-destructive hover:underline px-1"
                            title="Remove from PO"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </summary>
                      <div className="px-3 pb-3 pt-1 border-t">
                        <CustomFeesEditor
                          items={c.custom_fees || []}
                          onChange={(v) => setCarFees(c.car_id, v)}
                          canSeeInternal={canSeeInternal}
                        />
                      </div>
                    </details>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button type="button" variant="outline" onClick={() => setEditorOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="douane-save-btn">{editing ? 'Save changes' : 'Create PO'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DouanePosPage;
